import { getAuthUrl, getRestaurantSubdomain } from '@core/config/authDomain';
import { getCurrentLanguage } from '@core/i18n/config';
import { safeSessionStorage } from '@shared/utils/safeStorage';

/**
 * Minimal booking context to encode in the return URL as fallback
 * in case sessionStorage is lost during the cross-subdomain redirect.
 */
export interface BookingContext {
  diners?: number;
  selectedDate?: string | null;
  service?: string | null;
  selectedTime?: string | null;
  flowType?: string;
  specialEventSlug?: string | null;
  isWaitingList?: boolean;
}

/**
 * Build the return URL that the auth subdomain will redirect back to.
 *
 * - With booking context → /auth-return?ps=4&d=... (booking flow resume)
 * - Without booking context → current page URL (e.g. /, /profile)
 */
function buildReturnUrl(bookingContext?: BookingContext): string {
  const protocol = window.location.protocol;
  const host = window.location.host;

  // No booking context → return to current page (landing, profile, etc.)
  if (!bookingContext) {
    return `${protocol}//${host}${window.location.pathname}`;
  }

  // With booking context → return to /auth-return with fallback params
  const params = new URLSearchParams();
  if (bookingContext.diners) params.set('ps', String(bookingContext.diners));
  if (bookingContext.selectedDate) params.set('d', bookingContext.selectedDate);
  if (bookingContext.service) params.set('st', bookingContext.service);
  if (bookingContext.selectedTime) params.set('t', bookingContext.selectedTime);
  if (bookingContext.flowType && bookingContext.flowType !== 'standard') {
    params.set('ft', bookingContext.flowType);
  }
  if (bookingContext.specialEventSlug) params.set('evt', bookingContext.specialEventSlug);
  if (bookingContext.isWaitingList) params.set('wl', '1');

  const qs = params.toString();
  return `${protocol}//${host}/auth-return${qs ? `?${qs}` : ''}`;
}

/**
 * Redirect to the auth subdomain for login or signup.
 *
 * - From landing/profile: call without bookingContext → returns to same page
 * - From booking flow: call with bookingContext → returns to /auth-return
 */
export function redirectToAuth(
  flow: 'login' | 'signup',
  bookingContext?: BookingContext,
  /** Optional OAuth provider hint — auto-triggers the provider on the auth page */
  provider?: 'google' | 'facebook',
  /** ISO country code for the restaurant (e.g. 'UY', 'AR') — used for phone input defaults */
  country?: string,
): void {
  const restaurant = getRestaurantSubdomain();
  const lang = getCurrentLanguage();
  const returnUrl = buildReturnUrl(bookingContext);

  const resolvedCountry = country || safeSessionStorage.getItem('restaurant_country') || '';
  const dialCode = safeSessionStorage.getItem('restaurant_dial_code') || '';

  const params: Record<string, string> = {
    return_url: returnUrl,
    restaurant,
    lang,
  };
  if (provider) params.provider = provider;
  if (resolvedCountry) params.country = resolvedCountry;
  if (!resolvedCountry && dialCode) params.dial_code = dialCode;

  const authUrl = getAuthUrl(flow, params);

  window.location.replace(authUrl);
}
